import React, { useCallback } from 'react';
import { 
  MDXEditor as BaseMDXEditor, 
  RealmPlugin,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  linkPlugin,
  linkDialogPlugin,
  tablePlugin,
  frontmatterPlugin,
  codeBlockPlugin,
  sandpackPlugin,
  codeMirrorPlugin,
  diffSourcePlugin,
  toolbarPlugin,
  KitchenSinkToolbar,
  directivesPlugin,
  jsxPlugin
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { Box } from '@mui/material';
import { customImagePlugin, MDXEditorImageUploader } from './plugins/customImagePlugin';

interface MDXEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  plugins?: RealmPlugin[];
  contentEditableClassName?: string;
}

const MDXEditor: React.FC<MDXEditorProps> = ({ value, onChange, placeholder, readOnly = false, plugins = [], contentEditableClassName = "prose max-w-full" }) => {
  const handleChange = useCallback((newValue: string) => {
    if (!readOnly) {
      onChange(newValue);
    }
  }, [onChange, readOnly]);

  // Default plugins to include all functionality
  const defaultPlugins = [
    headingsPlugin(),
    listsPlugin(),
    quotePlugin(),
    thematicBreakPlugin(),
    markdownShortcutPlugin(),
    linkPlugin(),
    linkDialogPlugin(),
    customImagePlugin(),
    tablePlugin(),
    frontmatterPlugin(),
    codeBlockPlugin({
      defaultCodeBlockLanguage: 'plaintext'
    }),
    sandpackPlugin({
      sandpackConfig: {
        defaultPreset: "react",
        presets: [
          {
            name: "react",
            meta: "live react",
            label: "React",
            sandpackTemplate: "react",
            sandpackTheme: "light",
            snippetFileName: "/App.js",
            snippetLanguage: "jsx",
            initialSnippetContent: `
export default function App() {
  return (
    <div className="App">
      <h1>Hello CodeSandbox</h1>
      <h2>Start editing to see some magic happen!</h2>
    </div>
  );
}
`
          }
        ]
      }
    }),
    codeMirrorPlugin({
      codeBlockLanguages: {
        js: 'JavaScript',
        ts: 'TypeScript',
        tsx: 'TypeScript (React)',
        jsx: 'JavaScript (React)',
        css: 'CSS',
        html: 'HTML',
        python: 'Python',
        java: 'Java',
        ruby: 'Ruby',
        php: 'PHP',
        go: 'Go',
        rust: 'Rust',
        c: 'C',
        cpp: 'C++',
        csharp: 'C#',
        swift: 'Swift',
        kotlin: 'Kotlin',
        scala: 'Scala',
        shell: 'Shell',
        bash: 'Bash',
        powershell: 'PowerShell',
        sql: 'SQL',
        json: 'JSON',
        yaml: 'YAML',
        markdown: 'Markdown',
        plaintext: 'Plain Text',
        // Add an empty string key to handle code blocks without a specified language
        '': 'Plain Text'
      }
    }),
    directivesPlugin(),
    jsxPlugin(),
    diffSourcePlugin(), // For preview and edit modes
    toolbarPlugin({
      toolbarContents: () => <KitchenSinkToolbar /> // Include all toolbar components
    })
  ];

  // Combine default plugins with any additional plugins passed as props
  const allPlugins = [...defaultPlugins, ...plugins];

  return (
    <Box>
      <MDXEditorImageUploader>
        <BaseMDXEditor
          markdown={value}
          onChange={handleChange}
          placeholder={placeholder}
          readOnly={readOnly}
          plugins={allPlugins}
          contentEditableClassName={contentEditableClassName}
        />
      </MDXEditorImageUploader>
    </Box>
  );
};

export default MDXEditor;
